
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface StudentRequest {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  goals: string | null;
  status: string;
  trainer_id: string;
  created_at: string;
  updated_at: string;
  processed_at?: string | null;
  notes?: string | null;
}

export const useStudentRequests = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: studentRequests,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ["student-requests"],
    queryFn: async () => {
      console.log("Fetching student requests...");
      const { data, error } = await supabase
        .from("student_requests")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) {
        console.error("Error fetching student requests:", error);
        toast.error("Erro ao carregar pedidos de alunos");
        throw error;
      }
      
      console.log("Student requests fetched:", data);
      return data as StudentRequest[];
    }
  });

  const deleteRequest = async (id: string) => {
    try {
      console.log("Deleting student request with ID:", id);
      
      // Verificar se o ID é válido
      if (!id || typeof id !== 'string' || id.trim() === '') {
        console.error("Invalid ID provided for deletion:", id);
        toast.error("ID inválido para eliminação");
        return false;
      }

      // Executar a operação de eliminar sem a cláusula select("count")
      const { error } = await supabase
        .from("student_requests")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Error deleting student request:", error);
        toast.error("Erro ao eliminar pedido de aluno");
        return false;
      }

      console.log("Student request deleted successfully");
      toast.success("Pedido eliminado com sucesso");
      
      // Forçar uma nova consulta para atualizar a lista
      await refetch();
      return true;
    } catch (error) {
      console.error("Exception deleting student request:", error);
      toast.error("Erro ao eliminar pedido de aluno");
      return false;
    }
  };

  const filteredRequests = studentRequests?.filter(request => {
    const searchLower = searchTerm.toLowerCase();
    return (
      request.first_name?.toLowerCase().includes(searchLower) || 
      request.last_name?.toLowerCase().includes(searchLower) || 
      request.email?.toLowerCase().includes(searchLower)
    );
  });

  return {
    studentRequests,
    filteredRequests,
    isLoading,
    searchTerm,
    setSearchTerm,
    refetch,
    deleteRequest
  };
};
